/**
 * 官网新版图片资源映射。
 *
 * 除 shubiao.svg（光标 demo，依赖打包 hash 防缓存且体积极小）保留本地 import，
 * 其余 PC / Mobile 共用图片均替换为 OSS 远程 URL：
 *   https://bujue-static-cdn.bujue.art/官网图片/<图片名>
 *
 * 含中文 / 空格的文件名需 `encodeURIComponent`，否则部分浏览器会按非法 URL
 * 截断或重复转义；子目录（brand/）逐段编码，保留 `/`。
 */
import imgDemoCursor from "@assets/images/official_website_new/shubiao.svg";
import imgBujueLogo from "@assets/images/official_website_new/logo.png";
import imgBujueLogoWhite from "@assets/images/official_website_new/bujue_logo.png";
import imgWxKf from "@assets/images/wx_kf.png";
import imgKefuQr from "@assets/images/official_website_new/kefu.jpg";
import imgXiaohongshuQr from "@assets/images/official_website_new/xiaohongshu_code.png";
import imgGongzhonghaoQr from "@assets/images/official_website_new/gongzhonghao.jpg";
/* 功能区图标 / 社交图标：从本地 svg 引入（文件名含中文/括号，webpack 用 url-loader 直接处理） */
import iconServiceWebsite from "@assets/images/official_website_new/地球（细）.svg";
import iconServicePortfolio from "@assets/images/official_website_new/图片（细）.svg";
import iconServicePartners from "@assets/images/official_website_new/成员（细）.svg";
import iconServiceMiniProgram from "@assets/images/official_website_new/小程序（细）.svg";
import iconSocialWechat from "@assets/images/official_website_new/微信（填充）.svg";
import iconSocialXiaohongshu from "@assets/images/official_website_new/小红书.svg";
import iconPlay from "@assets/images/official_website_new/播放（细）.svg";
import iconArrowRight from "@assets/images/official_website_new/right_jiantou.svg";
import iconArrowRightPC from "@assets/images/official_website_new/单箭头（向右）（细）.svg";

const OSS_BASE =
  "https://bujue-static-cdn.bujue.art/%E5%AE%98%E7%BD%91%E5%9B%BE%E7%89%87/";

/** 编码图片相对路径（保留 `/`），用于拼接 OSS 完整 URL */
function oss(name) {
  return OSS_BASE + name.split("/").map(encodeURIComponent).join("/");
}

/** 单图入口：键名沿用原 `imgXxx` 风格，便于在 App.jsx 中替换引用 */
export const IMG = {
  banner1: oss("banner_1.png"),
  banner2: oss("banner_2.png"),
  banner3: oss("banner_6.png"),
  banner4: oss("banner_4.png"),
  banner5: oss("banner_5.png"),
  banner6: oss("banner_3.png"),
  solution1: oss("solution-1.jpg"),
  solution2: oss("solution-2.jpg"),
  heroScreen: oss("hero-screen.png"),
  heroCard1: oss("hero-card-1.png"),
  heroCard2: oss("hero-card-2.png"),
  heroCard3: oss("hero-card-3.jpg"),
  heroCard4: oss("hero-card-4.png"),
  createWeb: oss("create_web.svg"),
  generated1: oss("generated_1.png"),
  generated2: oss("generated_2.svg"),
  generated3: oss("generated_3.png"),
  private1: oss("private_1.svg"),
  private2: oss("private_2.svg"),
  miniProgramBg: oss("xiaochengxu_bg.svg"),
  xiaochengxu: oss("xiaochengxu_bg.png"),
  /** 品牌 logo：保留本地 import */
  bujueLogo: imgBujueLogo,
  bujueLogoWhite: imgBujueLogoWhite,
  /** 客服微信二维码：保留本地 import */
  wxKf: imgWxKf,
  /** Footer 社媒 hover 二维码 / 公众号弹框二维码：本地 import */
  kefuQr: imgKefuQr,
  xiaohongshuQr: imgXiaohongshuQr,
  gongzhonghaoQr: imgGongzhonghaoQr,
  /** 光标 demo：保留本地 import */
  demoCursor: imgDemoCursor,
  /** 功能区 4 个 icon（本地 svg） */
  serviceWebsite: iconServiceWebsite,
  servicePortfolio: iconServicePortfolio,
  servicePartners: iconServicePartners,
  serviceMiniProgram: iconServiceMiniProgram,
  /** 社交图标（本地 svg） */
  socialWechat: iconSocialWechat,
  socialXiaohongshu: iconSocialXiaohongshu,
  /** 播放图标（本地 svg） */
  play: iconPlay,
  /** 查看更多箭头：mobile 用 right_jiantou，PC 用原版 */
  arrowRight: iconArrowRight,
  arrowRightPC: iconArrowRightPC,
};

/** case-track 轮播图：banner_1 为默认主图 */
export const CASE_BG = [
  IMG.banner1,
  IMG.banner2,
  IMG.banner3,
  IMG.banner4,
  IMG.banner5,
  IMG.banner6,
];

export const GENERATED_PORTFOLIO_IMGS = [
  IMG.generated1,
  IMG.generated2,
  IMG.generated3,
];

export const PRIVATE_SPHERE_IMGS = [IMG.private1, IMG.private2];

/** 合作品牌跑马灯：原 `shared/logo_track_brand/` 下逐张 import 改为 OSS URL，
 *  顺序与原文件保持一致（Group / 拉丁 / 中文）。 */
export const SCROLL_BRAND_IMAGES = [
  oss("Group 49073.png"),
  oss("Group 49074.png"),
  oss("HBI 1.png"),
  oss("KOKORUG 1.png"),
  oss("MUZZI 1.png"),
  oss("OUR JOY 1.png"),
  oss("TASSANI 1.png"),
  oss("Visual Comfort&Co. 1.png"),
  oss("XIAO马达甄彩 1.png"),
  oss("sunshine 1.png"),
  oss("一道 1.png"),
  oss("依诺 1.png"),
  oss("佛山设计协会 1.png"),
  oss("巴博罗 1.png"),
  oss("庞玻 1.png"),
  oss("德赛斯 1.png"),
  oss("慕妮 1.png"),
  oss("戴璞建筑 1.png"),
  oss("墅标 1.png"),
  oss("瑞欣 1.png"),
  oss("费罗娜 1.png"),
  oss("贝尊 1.png"),
  oss("达观国际 1.png"),
  oss("金意陶 1.png"),
];
